import React from 'react';
import { UserProfile } from 'types/authContext';
import { useTranslation } from 'react-i18next';
import Text from '@ingka/text';
import Hyperlink from '@ingka/hyperlink';
import { translateWithFallback } from 'utils/translations';
import ProfilePicture from 'components/ProfilePicture';
import ListView, { ListViewItem } from '@ingka/list-view';
import Button from '@ingka/button';
import useAuth from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import classes from './UserInfoPanel.module.scss';

const Details = ({ title, data }: { title: string; data: string }) => (
    <div className={classes['user-info_detail']} data-testid="user-info-detail">
        <Text textStyle="Heading.XS">{title}</Text>
        <Text textStyle="Body.M">{data}</Text>
    </div>
);

const UserInfoPanel = ({ user }: { user: UserProfile | null }) => {
    const { authClient } = useAuth();
    const { displayToast } = useToast();
    const { t, i18n } = useTranslation();

    if (!user) {
        return (
            <div data-testid="user-info-panel">
                <div className={classes['user-info_profile']}>
                    <div className={classes['user-info_profile-pic']}>
                        <ProfilePicture imageSource={null} size="large" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="user-info-panel">
            <ListView id="userInfo-panel-details-list-view" data-testid="userInfo-panel-details-list-view">
                <ListViewItem
                    data-testid="userInfo-panel-details-profile"
                    emphasised
                    description={(
                        <div className={classes['user-info_profile']}>
                            <div className={classes['user-info_profile-pic']}>
                                <ProfilePicture imageSource={user?.image ?? null} size="large" />
                                <Text tagName="h6" textStyle="Heading.S" data-testid="user-info-name" className={classes['user-name']}>
                                    {user?.name ?? ''}
                                </Text>
                            </div>
                            <div>
                                <Button
                                    data-testid="user-info-modal-cancel-button"
                                    size="xsmall"
                                    text={t('SIGN_OUT')}
                                    type="secondary"
                                    onClick={() => authClient.logout().catch(() => { displayToast({ title: t('ERROR'), message: t('SIGN_OUT_FAILED') }); })}
                                />
                            </div>
                        </div>
                    )}
                    size="small"
                    showDivider={false}
                />
                <ListViewItem
                    data-testid="userInfo-panel-details-role"
                    emphasised
                    description={(
                        <div className={classes['user-info_role']}>
                            <Text tagName="h6" textStyle="Heading.XS">
                                {t('ROLE')}
                            </Text>
                            <div>
                                <ul>
                                    {user.role && user.role.length > 0 && user.role.map(roleItem => <li key={roleItem}>{roleItem}</li>)}
                                </ul>
                            </div>
                        </div>

                    )}
                    size="small"
                    showDivider={false}
                />
                <ListViewItem
                    data-testid="userInfo-panel-details-country"
                    emphasised
                    description={(
                        <Details
                            title={t('COUNTRY')}
                            data={translateWithFallback(`countryCodes.${user.country}`, user.country, t, i18n)}
                        />
                    )}
                    size="small"
                    showDivider={false}
                />
                <ListViewItem
                    data-testid="userInfo-panel-details-unit"
                    emphasised
                    description={(<Details title={t('UNIT')} data={user.businessUnit} />)}
                    size="small"
                    showDivider={false}
                />
                <ListViewItem
                    data-testid="userInfo-panel-details-office-location"
                    emphasised
                    description={(<Details title={t('OFFICE_LOCATION')} data={user.officeLocation} />)}
                    size="small"
                    showDivider={false}
                />
                <Hyperlink
                    text={t('PRIVACY_POLICY')}
                    className={classes['user-info_privacy-policy-link']}
                    key="privacy-policy"
                    rel="noreferrer"
                    url={process.env.VITE_APP_URLS_user_privacy_policy_url}
                    newWindow
                    target="_blank"
                />
            </ListView>
        </div>
    );
};

export default UserInfoPanel;
